import { zodResolver } from '@hookform/resolvers/zod'
import { useStore } from 'effector-react'
import { ComponentType, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  IdentityRecognitionDocumentEnum,
  IdentityRecognitionFileEnum,
} from '~/shared/config/enums'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { useTaxiparkQueryParams } from '~/shared/lib/useTaxiparkQueryParams'
import { formProviderModel } from '~/shared/ui/form'
import { useStorageRepeatCode } from '~/shared/ui/RepeatCode'
import { AdditionalDataStep, additionalDataFormModel } from './AdditionalData'
import { ConfirmCodeStep } from './ConfirmCodeStep'
import { TIMER_REGISTRATION, TIMER_REGISTRATION_TIME } from './constant'
import { CorrectionStep } from './CorrectionStep'
import { correctionFormModel } from './CorrectionStep'
import { extractCorrectionFields } from './CorrectionStep/ui/helpers'
import { DriverLicenseStep, driverLicenseFormModel } from './DriverLicenseStep'
import { ErrorStep } from './ErrorStep'
import { filteredDocumentsForRecognition } from './helpers'
import {
  sendCode,
  sendCodeFx,
  registration,
  registrationFx,
  validate,
} from './model'
import { PassportStep, passportFormModel } from './PassportStep'
import {
  sendPassportForRecognition,
  $recognitionErrors,
  $recognizedEntities,
} from './recognizedModel'
import { goToNextStep, $currentStepFlags, goToConfirmStep } from './stepModel'
import { Steps } from './Steps'

type RegistrationProps = {
  SuccessStep?: ComponentType
  additionalDataDefaultValues?: Partial<additionalDataFormModel.FormValues>
  disabledChangePhone?: boolean
  correctionDefaultValues?: Partial<correctionFormModel.FormValues>
}

export const DocumentBasedRegistration = ({
  SuccessStep,
  additionalDataDefaultValues,
  disabledChangePhone,
  correctionDefaultValues,
}: RegistrationProps) => {
  const { taxiparkIdQuery } = useTaxiparkQueryParams()
  const { runTimer, getIsTimerWorking } =
    useStorageRepeatCode(TIMER_REGISTRATION)
  const isSubmitting = useStore(registrationFx.pending)
  const isSendCodePending = useStore(sendCodeFx.pending)

  const {
    isAdditionalDataStep,
    isPassportStep,
    isDriverLicenseStep,
    isConfirmCodeStep,
    isSuccessStep,
    isErrorStep,
    isCorrectionStep,
  } = useStore($currentStepFlags)

  const passportForm = useForm<passportFormModel.FormValues>({
    resolver: zodResolver(passportFormModel.formSchema),
  })
  const driverLicenseForm = useForm<driverLicenseFormModel.FormValues>({
    resolver: zodResolver(driverLicenseFormModel.formSchema),
  })

  const additionalDataForm = useForm<additionalDataFormModel.FormValues>({
    resolver: zodResolver(additionalDataFormModel.formSchema),
    defaultValues: additionalDataDefaultValues,
  })

  const recognitionErrors = useStore($recognitionErrors)

  const recognizedEntitiesData = useStore($recognizedEntities)

  const requiredCorrectionKeys = useMemo(
    () =>
      recognitionErrors
        ? extractCorrectionFields(recognitionErrors).map(
            (f) => f.fieldName as keyof correctionFormModel.FormValues,
          )
        : [],
    [recognitionErrors],
  )

  const dynamicCorrectionSchema = useMemo(() => {
    const requiredMask = Object.fromEntries(
      requiredCorrectionKeys.map((k) => [k, true]),
    ) as Partial<Record<keyof correctionFormModel.FormValues, true>>

    return correctionFormModel.baseSchema.required(requiredMask)
  }, [requiredCorrectionKeys])

  const correctionForm = useForm<correctionFormModel.FormValues>({
    resolver: zodResolver(dynamicCorrectionSchema),
    defaultValues: correctionDefaultValues,
  })

  const [additionalData, setAdditionalData] = useState<
    Partial<additionalDataFormModel.FormValues>
  >({})
  const [passport, setPassport] = useState<
    Partial<passportFormModel.FormValues>
  >({})
  const [driverLicense, setDriverLicense] = useState<
    Partial<driverLicenseFormModel.FormValues>
  >({})

  const [correctionData, setCorrectionData] = useState<
    Partial<correctionFormModel.FormValues>
  >({})

  const phone = additionalData.phone

  const handlePassportOnSuccess = (
    formValues: passportFormModel.FormValues,
  ) => {
    setPassport(formValues)
    const passportPhotos = passportForm.getValues('passportPhotos')
    const files = filteredDocumentsForRecognition(passportPhotos, [
      IdentityRecognitionFileEnum.passport,
    ])
    sendPassportForRecognition({
      documentType: IdentityRecognitionDocumentEnum.passport,
      files,
      taxiparkId: taxiparkIdQuery,
    })
    goToNextStep()
  }

  const handleDriverLicenseOnSuccess = (
    formValues: driverLicenseFormModel.FormValues,
  ) => {
    setDriverLicense(formValues)

    const drivingLicensePhotos = driverLicenseForm.getValues(
      'drivingLicensePhotos',
    )
    const files = filteredDocumentsForRecognition(drivingLicensePhotos, [
      IdentityRecognitionFileEnum.driverLicenseFront,
      IdentityRecognitionFileEnum.driverLicenseBack,
    ])
    sendPassportForRecognition({
      documentType: IdentityRecognitionDocumentEnum.driverLicense,
      files,
      taxiparkId: taxiparkIdQuery,
    })
    goToNextStep()
  }

  const handleAdditionalDataOnSuccess = (
    formValues: additionalDataFormModel.FormValues,
  ) => {
    validate({
      formValues,
      taxiparkId: taxiparkIdQuery,
      formSetError:
        additionalDataForm.setError as formProviderModel.FormSetError,
      successFn: () => {
        setAdditionalData(formValues)
        if (recognitionErrors) {
          goToNextStep()
        } else {
          handleCodeRequest()
        }
      },
    })
  }

  const handleCorrectionDataOnSuccess = (
    formValues: correctionFormModel.FormValues,
  ) => {
    validate({
      formValues: {
        ...formValues,
        citizenship: passport.citizenship,
        drivingLicenseIssuingCountry:
          driverLicense.drivingLicenseIssuingCountry,
      },
      taxiparkId: taxiparkIdQuery,
      formSetError: correctionForm.setError as formProviderModel.FormSetError,
      successFn: () => {
        handleCodeRequest()
        setCorrectionData(formValues)
      },
    })
  }

  const handleSubmit = (code: string) => {
    if (!taxiparkIdQuery) {
      snackbarEnqueued({
        message: 'Ошибка получения ID таксопарка, обратитесь к администратору',
        variant: 'warning',
      })
      return
    }
    registration({
      taxiparkId: taxiparkIdQuery,
      confirmCode: code,
      ...recognizedEntitiesData,
      ...additionalData,
      ...passport,
      ...driverLicense,
      ...correctionData,
    })
  }

  const warningRequestCod = () => {
    snackbarEnqueued({
      message:
        'Запросите код повторно после того как пройдет блокировка получения кода',
      variant: 'warning',
    })
  }

  const handleCodeRequest = () => {
    if (getIsTimerWorking()) {
      warningRequestCod()
      goToConfirmStep()
    } else {
      sendCode({
        phone: phone as string,
        fn: () => {
          runTimer(TIMER_REGISTRATION_TIME)
          goToConfirmStep()
        },
      })
    }
  }

  return (
    <>
      {isPassportStep && (
        <PassportStep onSuccess={handlePassportOnSuccess} form={passportForm} />
      )}

      {isDriverLicenseStep && (
        <DriverLicenseStep
          onSuccess={handleDriverLicenseOnSuccess}
          form={driverLicenseForm}
          isLoading={isSendCodePending}
        />
      )}

      {isAdditionalDataStep && (
        <AdditionalDataStep
          onSuccess={handleAdditionalDataOnSuccess}
          form={additionalDataForm}
          disabledChangePhone={disabledChangePhone}
        />
      )}

      {isCorrectionStep && (
        <CorrectionStep
          onSuccess={handleCorrectionDataOnSuccess}
          form={correctionForm}
          errorsData={recognitionErrors}
        />
      )}

      {isConfirmCodeStep && (
        <ConfirmCodeStep
          phone={phone}
          onOk={handleSubmit}
          isSubmitting={isSubmitting}
          onCodeRepeat={handleCodeRequest}
        />
      )}

      {isSuccessStep && SuccessStep && <SuccessStep />}

      {isErrorStep && <ErrorStep />}

      {(isAdditionalDataStep || isPassportStep || isDriverLicenseStep) && (
        <Steps />
      )}
    </>
  )
}
